import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

export interface VolcesSeededit30Params {
  /**
   * 编辑提示词，长度必须在1-800个字符之间（建议≤120字符）
   */
  prompt: string;
  /**
   * Base64编码的图像数据数组，必须包含恰好1个图像，与image_urls二选一
   */
  binary_data_base64?: string[];
  /**
   * 图像URL数组，必须包含恰好1个图像，与binary_data_base64二选一
   */
  image_urls?: string[];
  /**
   * 随机种子，-1表示随机或使用正整数，默认值为-1
   * @default -1
   */
  seed?: number;
  /**
   * 编辑强度，取值范围：0-1，默认值为0.5
   * @default 0.5
   */
  scale?: number;
}

export interface VolcesSeededit30Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seededit_3_0
 *
 * Volces SeedEdit 3.0 Image Generator - 火山引擎图像编辑实现
 * 
 * Model: SeedEdit 3.0
 * Features: 图像编辑
 * Input: 1张参考图像
 * Prompt Length: 1-800字符（建议≤120）
 * 
 * API Documentation: https://www.volcengine.com/docs/85128/1602254
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeededit30 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeededit30({
 *   // 参数见 VolcesSeededit30Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeededit30Params 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeededit30(
  params: VolcesSeededit30Params,
): Promise<VolcesSeededit30Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seededit_3_0',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeededit30Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'image');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
